import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { queryKeys } from '@/lib/queryClient';

export interface SuggestionSource {
  type: 'featured' | 'playlist' | 'library_artist' | 'library_label' | 'mb_related';
  via: string;
  detail?: string;
}

export interface ChannelSuggestion {
  suggestedChannelId: string;
  name: string;
  thumbnailUrl?: string;
  description?: string;
  sources: SuggestionSource[];
  score: number;
}

export interface GroupedSuggestions {
  scenes: ChannelSuggestion[];
  library: ChannelSuggestion[];
  serendipity: ChannelSuggestion[];
}

export function useSuggestions() {
  return useQuery({
    queryKey: queryKeys.suggestions,
    queryFn: async (): Promise<GroupedSuggestions> => {
      const response = await api.get('/suggestions');
      return response.data as GroupedSuggestions;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useDismissSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      await api.post(`/suggestions/${channelId}/dismiss`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.suggestions }),
  });
}

export function useRefreshSuggestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/suggestions/refresh');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.suggestions }),
  });
}
